import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HouseholdIncome() {
  const { householdId } = useParams();
  const [incomeByPeriod, setIncomeByPeriod] = useState({});
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payDate, setPayDate] = useState(new Date());
  const [payFrequency, setPayFrequency] = useState('biweekly');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [newIncome, setNewIncome] = useState({
    name: '',
    amount: '',
    pay_period_start: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [householdRes, dashboardRes] = await Promise.all([
        fetch(`${API}/households/${householdId}`, { credentials: 'include' }),
        fetch(`${API}/households/${householdId}/dashboard?pay_date=${payDate.toISOString()}&pay_frequency=${payFrequency}&force_three=false`, {
          method: 'POST',
          credentials: 'include',
        })
      ]);
      
      if (householdRes.ok) {
        const data = await householdRes.json();
        setHousehold(data);
      }
      
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        // Group income by period
        const grouped = {};
        data.periods?.forEach(period => {
          grouped[period.period_start_iso] = {
            ...period,
            income_sources: period.income_sources || []
          };
        });
        setIncomeByPeriod(grouped);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [householdId, payDate, payFrequency]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddIncome = async () => {
    if (!newIncome.name || !newIncome.amount || !selectedPeriod) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/households/${householdId}/income`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newIncome.name,
          amount: parseFloat(newIncome.amount),
          pay_period_start: selectedPeriod,
        }),
      });
      
      if (response.ok) {
        setNewIncome({ name: '', amount: '', pay_period_start: '' });
        setShowAddForm(false);
        setSelectedPeriod(null);
        loadData();
      }
    } catch (error) {
      console.error('Error adding income:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIncome = async (incomeId) => {
    if (!window.confirm('Delete this shared income entry?')) return;
    
    try {
      const response = await fetch(`${API}/households/${householdId}/income/${incomeId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error deleting income:', error);
    }
  };

  const totalIncome = Object.values(incomeByPeriod).reduce(
    (sum, period) => sum + (period.income || 0), 
    0
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Link to={`/household/${householdId}/dashboard`} className="text-gray-500 hover:text-gray-700 text-sm">
          ← Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2" data-testid="household-income-title">
          💵 Shared Income - {household?.name}
        </h1>
        <p className="text-gray-600">Track income contributed by all household members</p>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-green-100 text-sm">Total Shared Income</p>
            <p className="text-4xl font-bold">${totalIncome.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Pay Schedule Settings */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <h2 className="font-bold text-gray-900 mb-4">⚙️ Pay Schedule</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Pay Frequency</label>
            <select
              value={payFrequency}
              onChange={(e) => setPayFrequency(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl"
            >
              <option value="biweekly">Every 2 weeks</option>
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">First Pay Date</label>
            <input
              type="date"
              value={format(payDate, 'yyyy-MM-dd')}
              onChange={(e) => setPayDate(new Date(e.target.value + 'T12:00:00'))}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl"
            />
          </div>
        </div>
      </div>

      {/* Pay Periods with Income */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900">📅 Income by Pay Period</h2>
        
        {Object.entries(incomeByPeriod).length > 0 ? (
          Object.entries(incomeByPeriod).map(([periodKey, period]) => (
            <div key={periodKey} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              {/* Period Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-emerald-100 text-sm">Pay Period #{period.period}</p>
                    <h3 className="text-lg font-bold">{period.date} — {period.end_date}</h3>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-100 text-sm">Period Income</p>
                    <p className="text-2xl font-bold">${period.income?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </div>

              {/* Income List */}
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-700">Income Sources</h4>
                  <button
                    onClick={() => {
                      setSelectedPeriod(periodKey);
                      setShowAddForm(true);
                    }}
                    className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                    data-testid={`add-income-btn-${period.period}`}
                  >
                    + Add Income
                  </button>
                </div>

                {/* Add Income Form for this period */}
                {showAddForm && selectedPeriod === periodKey && (
                  <div className="mb-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                    <p className="text-sm font-medium text-emerald-800 mb-3">Add new income for this period</p>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={newIncome.name}
                        onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
                        placeholder="Income source (e.g., Paycheck, Side job)"
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl"
                        data-testid="income-name-input"
                      />
                      <div className="flex gap-3">
                        <input
                          type="number"
                          value={newIncome.amount}
                          onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                          placeholder="Enter amount"
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-xl"
                          data-testid="income-amount-input"
                        />
                        <button
                          onClick={handleAddIncome}
                          disabled={saving || !newIncome.name || !newIncome.amount}
                          className="px-6 py-2 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50"
                          data-testid="save-income-btn"
                        >
                          {saving ? 'Adding...' : 'Add'}
                        </button>
                        <button
                          onClick={() => {
                            setShowAddForm(false);
                            setSelectedPeriod(null);
                            setNewIncome({ name: '', amount: '', pay_period_start: '' });
                          }}
                          className="px-4 py-2 text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {period.income_sources?.length > 0 ? (
                  <div className="space-y-2">
                    {period.income_sources.map((income) => (
                      <div key={income.id} className="flex items-center justify-between p-4 bg-green-50 rounded-xl" data-testid={`income-item-${income.id}`}>
                        <div>
                          <p className="font-medium text-gray-900">{income.name}</p>
                          <p className="text-sm text-gray-500">
                            Added by {income.contributor || 'Member'}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-bold text-green-600 text-lg">${income.amount?.toLocaleString()}</span>
                          <button
                            onClick={() => handleDeleteIncome(income.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            data-testid={`delete-income-${income.id}`}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <div className="text-4xl mb-2">💵</div>
                    <p>No income added for this period yet</p>
                    <p className="text-sm text-gray-400 mt-1">Click &quot;+ Add Income&quot; above to add</p>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
            <div className="text-5xl mb-4">💵</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No pay periods loaded</h2>
            <p className="text-gray-600 mb-4">Set your pay schedule above to see pay periods</p>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
        <h3 className="font-bold text-gray-900 mb-2">💡 How Shared Income Works</h3>
        <ul className="text-gray-600 text-sm space-y-2">
          <li>• Each household member can add their income for any pay period</li>
          <li>• All income is visible to everyone in the household</li>
          <li>• Income automatically reflects on the household dashboard</li>
          <li>• Use this to track combined family income for shared budgeting</li>
        </ul>
      </div>
    </div>
  );
}

export default HouseholdIncome;
